import React, { useEffect, useMemo, useState } from 'react';
import Modal from './ui/Modal';
import { Calendar, Plus } from 'lucide-react';
import apiService from '../services/apiService';
import TicketCustomerField from './TicketCustomerField';
import { CalenderMarkerHeaderSelect } from '../utils/calenderMarkerUtils';
import { useI18n } from '../utils/i18n';
import { flagInvalidField } from '../utils/formValidation';
import { ticketPasstZurSuche } from '../utils/suchFilter';
import { useFeature } from '../utils/featuresCache';
import { useToast } from './ToastProvider';

const defaultNewTicket = {
  title: '',
  description: '',
  priority: 'normal',
  category: 'Kalender',
  calenderMarker: null,
  assignedTo: '',
  // #210: bei Terminen gibt es keine verlässliche Absenderdomäne — Kunde
  // wird hier immer manuell gewählt (keine Vorbelegung)
  customerId: null,
  contactId: null
};

function ensureUtc(dateTimeString) {
  try {
    if (!dateTimeString) return null;
    return dateTimeString.endsWith('Z') ? dateTimeString : `${dateTimeString}Z`;
  } catch (_) {
    return null;
  }
}

function htmlToPlainText(html) {
  try {
    if (!html || typeof window === 'undefined' || !window.document) return String(html || '');
    const div = window.document.createElement('div');
    div.innerHTML = String(html);
    
    // Entferne <style>, <script> und andere nicht-textuelle Elemente komplett
    div.querySelectorAll('style, script, head, noscript').forEach(el => el.remove());
    
    // Ersetze <br> und Blockelemente grob durch Zeilenumbrüche, bevor innerText gelesen wird
    div.querySelectorAll('br').forEach(br => br.replaceWith(window.document.createTextNode('\n')));
    div.querySelectorAll('p').forEach(p => {
      const newline = window.document.createTextNode('\n');
      p.parentNode && p.parentNode.insertBefore(newline, p.nextSibling);
    });
    const text = div.innerText;
    return text.replace(/\n{3,}/g, '\n\n').trim();
  } catch (_) {
    // Fallback: naive Strip der Tags
    return String(html || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
  }
}

// Simple helper: parse "Base (n)" suffix like in calendar views
function parseBlockSuffix(title) {
  try {
    const raw = String(title || '');
    const m = raw.match(/\s*\((\d+)\)\s*$/);
    if (m) {
      const num = parseInt(m[1], 10);
      const baseTitle = raw.replace(/\s*\(\d+\)\s*$/, '').trim();
      return { baseTitle, existingBlock: (Number.isNaN(num) ? null : num) };
    }
    return { baseTitle: raw.trim(), existingBlock: null };
  } catch (_) {
    return { baseTitle: String(title || '').trim(), existingBlock: null };
  }
}

const EventToTicketModal = ({
  visible,
  onClose,
  event,
  currentUser = null,
  targetUserEmail = null,
  onSuccess = null
}) => {
  const { t } = useI18n();
  const kundenAktiv = useFeature('customers'); // #251: Platzhalter nennt Kunde nur, wenn der Kundenstamm an ist
  const { pushToast } = useToast();

  const [openTickets, setOpenTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketFilterText, setTicketFilterText] = useState('');

  const [showCreateTicketForm, setShowCreateTicketForm] = useState(false);
  const [newTicketData, setNewTicketData] = useState(defaultNewTicket);
  const [availableEmails, setAvailableEmails] = useState([]);
  const [loading, setLoading] = useState(false);

  const startIso = useMemo(() => {
    try {
      const s = ensureUtc(event?.start?.dateTime);
      return s ? new Date(s).toISOString() : null;
    } catch (_) { return null; }
  }, [event]);

  const endIso = useMemo(() => {
    try {
      const e = ensureUtc(event?.end?.dateTime);
      return e ? new Date(e).toISOString() : null;
    } catch (_) { return null; }
  }, [event]);

  const assignEmail = useMemo(() => {
    if (targetUserEmail && String(targetUserEmail).length > 0) return targetUserEmail;
    const cu = currentUser || {};
    return cu.mail || cu.userPrincipalName || cu.username || '';
  }, [currentUser, targetUserEmail]);

  const loadTickets = async () => {
    setLoadingTickets(true);
    try {
      const res = await apiService.getOpenTickets();
      setOpenTickets(res.tickets || []);
    } catch (e) {
      console.error('EventToTicketModal: error loading open tickets', e);
      setOpenTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const loadAvailableEmails = async () => {
    try {
      const emailsResponse = await apiService.getAvailableEmails();
      setAvailableEmails(emailsResponse.emails || []);
    } catch (error) {
      console.error('EventToTicketModal: error loading available emails', error);
      setAvailableEmails([]);
    }
  };

  useEffect(() => {
    if (visible) {
      setTicketFilterText('');
      setShowCreateTicketForm(false);
      const fullBodyHtml = (event?.body?.content || '');
      const fullBody = fullBodyHtml ? htmlToPlainText(fullBodyHtml) : (event?.bodyPreview || '');
      const joinUrl = (event?.onlineMeeting?.joinUrl || event?.onlineMeetingUrl || null);
      const composedDesc = joinUrl && fullBody && !fullBody.includes(joinUrl)
        ? `${fullBody}\n\n${joinUrl}`
        : (fullBody || joinUrl || '');
      setNewTicketData(prev => ({
        ...defaultNewTicket,
        title: event?.subject || 'Neues Ticket',
        description: composedDesc
      }));
      loadTickets();
    }
  }, [visible, event]);

  useEffect(() => {
    if (visible && showCreateTicketForm && !assignEmail) {
      // Nur laden, wenn keine feste Zuordnung erzwungen ist
      loadAvailableEmails();
    }
  }, [visible, showCreateTicketForm, assignEmail]);

  const filteredTickets = useMemo(() => {
    // #251: Nummer, Titel, Kunde, Ansprechpartner -- gemeinsamer Helfer
    if (!(ticketFilterText || '').trim()) return openTickets;
    return openTickets.filter(t => ticketPasstZurSuche(t, ticketFilterText));
  }, [ticketFilterText, openTickets]);

  const handleCreateNewTicket = () => {
    setShowCreateTicketForm(true);
  };

  const handleCancelCreateTicket = () => {
    setShowCreateTicketForm(false);
    setNewTicketData(defaultNewTicket);
  };

  const handleSubmitNewTicket = async (e) => {
    e.preventDefault();
    if (!event || !startIso || !endIso) {
      pushToast({ type: 'error', message: t('eventToTicket.invalidEvent', 'Fehler: Ungültiger Termin.') });
      return;
    }
    if (!newTicketData.title.trim()) {
      flagInvalidField(e.currentTarget, '#event-ticket-title', t('dashboard.enterTitle', 'Bitte geben Sie einen Titel ein.')) || pushToast({ type: 'error', message: t('dashboard.enterTitle', 'Bitte geben Sie einen Titel ein.') });
      return;
    }
    setLoading(true);
    try {
      const payload = { ...newTicketData };
      if (assignEmail) payload.assignedTo = assignEmail;
      const response = await apiService.createTicket(payload);
      const created = response.ticket || response;
      const planningData = {
        startDateTime: startIso,
        endDateTime: endIso,
        assignedTo: assignEmail || undefined,
        calendarEventId: event.id
      };
      
      // Verwende planTicketForUser wenn targetUserEmail gesetzt ist, sonst planTicket
      if (targetUserEmail && String(targetUserEmail).length > 0) {
        await apiService.planTicketForUser(created.id, planningData, targetUserEmail);
      } else {
        await apiService.planTicket(created.id, planningData);
      }
      
      // Delete original Graph event (current user or specific user)
      try {
        if (targetUserEmail && String(targetUserEmail).length > 0) {
          await apiService.deleteCalendarEventForUser(targetUserEmail, event.id);
        } else {
          await apiService.deleteCalendarEvent(event.id);
        }
      } catch (delErr) {
        console.warn('EventToTicketModal: could not delete original calendar event', delErr);
      }
      onClose && onClose();
      onSuccess && onSuccess();
    } catch (error) {
      console.error('EventToTicketModal: error creating ticket from event', error);
      pushToast({ type: 'error', message: t('eventToTicket.createPlanError', 'Fehler beim Erstellen/Planen des Tickets.') });
    } finally {
      setLoading(false);
    }
  };

  const handleAddEventToExistingTicket = async (ticket) => {
    if (!event || !startIso || !endIso) {
      pushToast({ type: 'error', message: t('eventToTicket.invalidEvent', 'Fehler: Ungültiger Termin.') });
      return;
    }
    setLoading(true);
    try {
      // 1) Projekt sicherstellen
      let projectId = null;
      if ((ticket.projectId && Number(ticket.projectId) > 0) || (ticket.subprojectId && Number(ticket.subprojectId) > 0)) {
        projectId = Number(ticket.projectId) || null;
      } else {
        const projectName = ticket.title || 'Projekt';
        const projectRes = await apiService.createProject({
          name: projectName,
          description: '',
          responsibleEmail: null,
          status: 'wartend',
          turnusCount: null,
          turnusEvery: null,
          turnusUnit: null,
          subprojects: [],
          isAutoCreated: true,
          // #230 (L5): Auto-Vorgang übernimmt den Kunden des Quelltickets (wie #158 in CalendarView)
          customerId: ticket.customerId || null
        });
        projectId = projectRes?.project?.id;
        if (!projectId) throw new Error('Projekt konnte nicht erstellt werden');
      }

      // 2) Originaltitel ggf. normalisieren auf (1)
      const { baseTitle, existingBlock } = parseBlockSuffix(ticket.title);
      const normalizedBase = baseTitle.replace(/\s*-\s*$/, '');
      const shouldRenameOriginal = (existingBlock == null);
      const newOriginalTitle = shouldRenameOriginal ? `${normalizedBase} - (1)` : ticket.title;
      if (shouldRenameOriginal) {
        await apiService.updateTicket(ticket.id, { title: newOriginalTitle, projectId, subprojectId: null });
      } else if (!ticket.projectId) {
        // Falls Block existiert aber kein Projekt gesetzt war
        await apiService.updateTicket(ticket.id, { projectId, subprojectId: null });
      }

      // 3) Zweites Ticket anlegen
      const nextBlockNum = (existingBlock ? existingBlock + 1 : 2);
      const block2Title = `${normalizedBase} - (${nextBlockNum})`;
      const assignedTo = ticket.assignedTo || assignEmail || '';
      const fullBodyHtml = (event?.body?.content || '');
      const fullBody = fullBodyHtml ? htmlToPlainText(fullBodyHtml) : (event?.bodyPreview || '');
      const joinUrl = (event?.onlineMeeting?.joinUrl || event?.onlineMeetingUrl || null);
      const composedDesc = joinUrl && fullBody && !fullBody.includes(joinUrl)
        ? `${fullBody}\n\n${joinUrl}`
        : (fullBody || joinUrl || (ticket.description || ''));
      const created2 = await apiService.createTicket({
        title: block2Title,
        description: composedDesc,
        priority: ticket.priority || 'normal',
        assignedTo: assignedTo || '',
        category: ticket.category || 'Projekt',
        tags: ticket.tags || [],
        calenderMarker: ticket.calenderMarker || null,
        projectId: projectId,
        subprojectId: null,
        // #210: Block-2-Ticket erbt Kunde/Ansprechpartner des Quelltickets (wie #158)
        customerId: ticket.customerId || null,
        contactId: ticket.contactId || null
      });
      const block2Ticket = created2.ticket || created2;

      // 4) Am Event-Zeitpunkt planen und mit calendarEventId verknüpfen
      const planningData = {
        startDateTime: startIso,
        endDateTime: endIso,
        assignedTo: assignedTo || undefined,
        calendarEventId: event.id
      };
      
      // Verwende planTicketForUser wenn targetUserEmail gesetzt ist, sonst planTicket
      if (targetUserEmail && String(targetUserEmail).length > 0) {
        await apiService.planTicketForUser(block2Ticket.id, planningData, targetUserEmail);
      } else {
        await apiService.planTicket(block2Ticket.id, planningData);
      }
      
      // Delete original Graph event
      try {
        if (targetUserEmail && String(targetUserEmail).length > 0) {
          await apiService.deleteCalendarEventForUser(targetUserEmail, event.id);
        } else {
          await apiService.deleteCalendarEvent(event.id);
        }
      } catch (delErr) {
        console.warn('EventToTicketModal: could not delete original calendar event', delErr);
      }

      onClose && onClose();
      onSuccess && onSuccess();
    } catch (err) {
      console.error('EventToTicketModal: add to existing ticket error', err);
      pushToast({ type: 'error', message: t('calendar.createError', 'Fehler beim Erstellen.') });
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <Modal
      open={visible}
      onClose={() => onClose && onClose()}
      title={<><Calendar size={24} /><h3>{t('event.addToTicket.modalTitle', 'Termin zu Ticket hinzufügen')}</h3>{showCreateTicketForm && (<CalenderMarkerHeaderSelect value={newTicketData.calenderMarker} onChange={(value) => setNewTicketData({ ...newTicketData, calenderMarker: value })} />)}</>}
      ariaLabel={t('event.addToTicket.modalTitle', 'Termin zu Ticket hinzufügen')}
      maxWidth="600px"
      footer={showCreateTicketForm ? (
        <>
          <button type="button" onClick={handleCancelCreateTicket} className="btn btn-secondary">{t('common.back', 'Zurück')}</button>
          <button type="submit" form="event-to-ticket-form" className="btn btn-primary" disabled={loading}>{loading ? t('common.creating', 'Erstelle...') : t('ticket.create.submit', 'Ticket erstellen')}</button>
        </>
      ) : (
        <button type="button" onClick={() => onClose && onClose()} className="btn btn-secondary">{t('common.cancel', 'Abbrechen')}</button>
      )}
    >

        {showCreateTicketForm ? (
          <form id="event-to-ticket-form" onSubmit={handleSubmitNewTicket} className="create-ticket-form">
            <div className="form-body">
              <h4>{t('ticket.create.new', 'Neues Ticket erstellen:')}</h4>
              {/* #210: Kunde/Ansprechpartner wie in der Ticketmaske (#104).
                  Funktionales setState: Kundenwechsel feuert zwei Resets (#105). */}
              <TicketCustomerField
                value={newTicketData.customerId}
                onChange={(customerId) => setNewTicketData(prev => ({ ...prev, customerId }))}
                contactValue={newTicketData.contactId}
                onContactChange={(contactId) => setNewTicketData(prev => ({ ...prev, contactId }))}
                inputId="event-ticket-customer"
                contactInputId="event-ticket-contact"
              />
              <div className="form-group">
                <label>{t('form.title', 'Titel')}</label>
                <input id="event-ticket-title" type="text" value={newTicketData.title} onChange={(e) => setNewTicketData({ ...newTicketData, title: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>{t('form.description', 'Beschreibung')}</label>
                <textarea rows="4" value={newTicketData.description} onChange={(e) => setNewTicketData({ ...newTicketData, description: e.target.value })} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('form.priority', 'Priorität')}</label>
                  <select value={newTicketData.priority} onChange={(e) => setNewTicketData({ ...newTicketData, priority: e.target.value })}>
                    <option value="niedrig">{t('priority.low', 'Niedrig')}</option>
                    <option value="normal">{t('priority.normal', 'Normal')}</option>
                    <option value="hoch">{t('priority.high', 'Hoch')}</option>
                    <option value="kritisch">{t('priority.critical', 'Kritisch')}</option>
                  </select>
                </div>
              </div>
              {assignEmail ? (
                <div className="form-group">
                  <label>{t('form.assignedTo', 'Zugewiesen an')}</label>
                  <div>{assignEmail}</div>
                </div>
              ) : (
                <div className="form-group">
                  <label>{t('form.assignedTo', 'Zugewiesen an')}</label>
                  <select value={newTicketData.assignedTo} onChange={(e) => setNewTicketData({ ...newTicketData, assignedTo: e.target.value })}>
                    <option value="">{t('common.unassigned', 'Nicht zugewiesen')}</option>
                    {availableEmails.map(email => (
                      <option key={email} value={email}>{email}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </form>
        ) : (
          <div className="ticket-selector">
            <h4>{t('ticket.addToExisting', 'Zu vorhandenem Ticket hinzufügen:')}</h4>
            <div className="ticket-filter">
              <input type="text" placeholder={(kundenAktiv ? t('ticket.filter.placeholderCustomer', 'Nach Ticketnummer, Titel, Kunde oder Ansprechpartner suchen...') : t('ticket.filter.placeholder', 'Nach Ticketnummer oder Titel suchen...'))} value={ticketFilterText} onChange={(e) => setTicketFilterText(e.target.value)} className="ticket-filter-input" />
            </div>
            {loadingTickets ? (
              <p>{t('ticket.loading', 'Lade Tickets...')}</p>
            ) : openTickets.length === 0 ? (
              <p>{t('ticket.noneOpen', 'Keine offenen Tickets gefunden.')}</p>
            ) : filteredTickets.length === 0 ? (
              <p>{t('ticket.noneMatchFilter', 'Keine Tickets gefunden, die dem Filter entsprechen.')}</p>
            ) : (
              <div className="ticket-list-scrollable">
                {filteredTickets.map(tk => (
                  <div key={tk.id} className="ticket-item-compact">
                    <div className="ticket-info-compact">
                      <div className="ticket-header-compact">
                        <span className="ticket-number-compact">#{tk.ticketNumber}</span>
                        <div className="ticket-badges-compact">
                          <span className={`status-badge-compact status-${tk.status}`}>{tk.status}</span>
                          <span className={`priority-badge-compact priority-${tk.priority}`}>{tk.priority}</span>
                        </div>
                      </div>
                      <div className="ticket-title-compact">{tk.title}</div>
                    </div>
                    <button onClick={() => handleAddEventToExistingTicket(tk)} className="btn btn-secondary btn-compact" disabled={loading}>
                      {loading ? t('common.adding', 'Füge hinzu...') : t('common.add', 'Hinzufügen')}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="create-ticket-option">
              <button onClick={handleCreateNewTicket} className="btn btn-primary">
                <Plus size={16} />
                {t('ticket.create.button', 'Neues Ticket erstellen')}
              </button>
            </div>
          </div>
        )}
    </Modal>
  );
};

export default EventToTicketModal;


